/**
 * @fileoverview Interactive help system with searchable topics and content display.
 * @module ui/HelpViewer
 */

import { updateElementVisibilityBasedOnValue, replaceKeyValuesInText, stripHtmlTags, escapeRegex, escapeHtml, assertTrustedAppDataUrl } from '../utils/jrfuncs.js';
import { VERSION_INFO, HELP_HTML_URL, DOM_ELEMENTS } from '../constants.js';

const { ID_HELP_SEARCH, ID_HELP_SEARCH_CLEAR, ID_HELP_TOPIC_CONTENT, ID_HELP_TOPICS_LIST,
    CLASS_HELP_TOPIC_ITEM } = DOM_ELEMENTS;
import { StatefulViewer } from './StatefulViewer.js';


/**
 * Interactive help viewer with topic navigation, search, and content display.
 * Supports state persistence for last viewed topic and search queries.
 * @class
 * @extends StatefulViewer
 */
export class HelpViewer extends StatefulViewer {
    /** @constant {string} Modal element ID */
    static MODAL_ID = 'helpModal';

    /**
     * Creates a new HelpViewer instance.
     * @param {Object} mapApp - The MapApp instance
     * @param {string} containerId - ID of the container element for the help viewer
     * @param {Object} helpConfig - Help configuration with topics array
     * @param {Array<Object>} helpConfig.topics - Array of topic objects
     */
    constructor(mapApp, containerId, helpConfig) {
        super(mapApp, containerId);

        this.helpConfig = helpConfig;
        this.topics = helpConfig.topics || [];
        this.currentTopic = null;
        this.helpSearchQuery = '';

        this.renderHelp();
        this.setupEventListeners();

        // Restore saved state if available
        const stateManager = this.getStateManager();
        if (stateManager) {
            const savedSearchQuery = stateManager.getHelpSearchQuery();
            const savedTopicId = stateManager.getHelpLastTopic();

            if (savedSearchQuery) {
                // Restore search query
                this.setAndPerformHelpSearch(savedSearchQuery);
            } else if (savedTopicId) {
                // Restore last topic if no search query
                this.showTopic(savedTopicId);
            } else if (this.topics.length > 0) {
                // Show first topic by default
                this.showTopic(this.topics[0].id);
            }
        } else if (this.topics.length > 0) {
            // Show first topic by default
            this.showTopic(this.topics[0].id);
        }
    }




    // ==========================================
    // SETUP
    // ==========================================

    /**
     * Sets up event listeners for search input, clear button, topic clicks, and command links.
     * @private
     */
    setupEventListeners() {
        // Idempotent: updateConfig() calls this again after re-rendering. The search input, clear
        // button and topic items are re-created by renderHelp(), so binding them again binds new
        // elements only (an element already bound is skipped); the container itself is the same
        // static element every time, so its click delegation is bound once per viewer.
        if (!this.boundElements) {
            this.boundElements = new WeakSet();
        }
        const bindOnce = (el, eventName, handler) => {
            if (!el || this.boundElements.has(el)) return;
            el.addEventListener(eventName, handler);
            this.boundElements.add(el);
        };

        // Search input
        const searchInput = this.container.querySelector('#' + ID_HELP_SEARCH);
        const clearButton = this.container.querySelector('#' + ID_HELP_SEARCH_CLEAR);

        bindOnce(searchInput, 'input', (e) => {
            this.performHelpSearch(e.target.value);
        });

        // Clear button
        bindOnce(clearButton, 'click', () => {
            if (searchInput) {
                searchInput.value = '';
                this.performHelpSearch('');
                searchInput.focus();
            }
        });

        // Topic clicks
        this.attachTopicClickListeners();

        // Event delegation for help/app commands in dynamically inserted content
        bindOnce(this.container, 'click', (e) => {
            const link = e.target.closest('[data-help-command], [data-app-command]');
            if (link) {
                e.preventDefault();
                const helpCmd = link.dataset.helpCommand;
                const appCmd = link.dataset.appCommand;
                let param = link.dataset.commandParam;

                // a way to auto grab code example and put it into param
                param = this.replaceUsingNearbyElementText(param, e.target, link);

                if (helpCmd) {
                    this.handleHelpCommand(helpCmd, param);
                } else if (appCmd) {
                    this.handleAppCommand(appCmd, param);
                }
            }
        });
    }


    /**
     * Attaches click listeners to topic items in the sidebar.
     * @private
     */
    attachTopicClickListeners() {
        if (!this.boundElements) {
            this.boundElements = new WeakSet();
        }
        const topicItems = this.container.querySelectorAll('.' + CLASS_HELP_TOPIC_ITEM);
        topicItems.forEach(item => {
            // (a re-rendered list has new items; an item already bound is not bound twice)
            if (this.boundElements.has(item)) return;
            item.addEventListener('click', () => {
                const topicId = item.dataset.topicId;
                this.showTopic(topicId);
            });
            this.boundElements.add(item);
        });
    }



    // ==========================================
    // HELPERS
    // ==========================================


    /**
     * Replaces placeholder tokens in text with content from nearby DOM elements.
     * Supports `${nextJrCodeExample}` which is replaced with the text content
     * of a `.jrcodeExample` span found within the event link.
     * @param {string} text - The text containing placeholder tokens
     * @param {HTMLElement} eventTarget - The element that triggered the event
     * @param {HTMLElement} eventLink - The parent link element containing the placeholder source
     * @returns {string} Text with placeholders replaced by element content
     * @private
     */
    replaceUsingNearbyElementText(text, eventTarget, eventLink) {
        // a command link with no data-command-param (the "Open settings dialog now" link) passes
        // undefined: that is no text to replace in, not an error (text.includes used to throw,
        // so the link did nothing)
        if (typeof text !== 'string') {
            return text;
        }
        const nextJrCodeExampleMatchString = '${nextJrCodeExample}';
        if (text.includes(nextJrCodeExampleMatchString)) {
            // Find the span with class "jrcodeExample" inside the event link and get its text
            const codeExampleSpan = eventLink.querySelector('span.jrcodeExample');
            const nextCodeReplacementText = codeExampleSpan ? codeExampleSpan.textContent : '';
            text = text.replaceAll(nextJrCodeExampleMatchString, nextCodeReplacementText);
        }
        return text;
    }


    /**
     * Parse HTML help file and convert to help config format
     * @param {string} htmlContent - The HTML content to parse
     * @returns {Object} Help configuration object with topics array
     */
    static parseHtmlToConfig(htmlContent) {
        // do replacements for verison info, etc.
        htmlContent = this.replaceConstantsInHtml(htmlContent);

        // Create a temporary DOM element to parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');

        const topics = [];
        const h1Elements = doc.querySelectorAll('h1');

        h1Elements.forEach((h1, index) => {
            // Extract topic data from h1 element
            const id = h1.id || `topic-${index}`;
            const icon = h1.getAttribute('data-icon') || 'bi-question-circle';
            const title = h1.textContent.trim();

            // Collect content until the next h1 or end of document
            let contentHtml = '';
            let currentNode = h1.nextSibling;

            while (currentNode && currentNode.nodeName !== 'H1') {
                if (currentNode.nodeType === Node.ELEMENT_NODE) {
                    contentHtml += currentNode.outerHTML;
                } else if (currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent.trim()) {
                    contentHtml += currentNode.textContent;
                }
                currentNode = currentNode.nextSibling;
            }

            const content = contentHtml.trim();
            topics.push({
                id: id,
                title: title,
                icon: icon,
                content: content,
                // the topic's plain text, lowercased, for the help search: worked out ONCE here, not
                // re-parsed for every topic at every keystroke (see getTopicSearchText)
                searchText: stripHtmlTags(content).toLowerCase()
            });
        });

        return { topics: topics };
    }



    /**
     * Replaces version info and other constant placeholders in help HTML content.
     * @param {string} htmlContent - The HTML content with placeholder keys
     * @returns {string} HTML content with placeholders replaced by constant values
     * @static
     * @private
     */
    static replaceConstantsInHtml(htmlContent) {
        // TODO: replace some version info text in help text
        const keyVals = {
            'VERSION_INFO.CbmVersionString': VERSION_INFO.CbmVersionString,
            'VERSION_INFO.CbmVersionDate': VERSION_INFO.CbmVersionDate,
            'VERSION_INFO.CbmContact' : VERSION_INFO.CbmContact,
        }

        htmlContent = replaceKeyValuesInText(htmlContent, keyVals);

        // return it
        return htmlContent;
    }


    /**
     * Loads help configuration by fetching and parsing an HTML file.
     * The HTML is converted to a configuration object via parseHtmlToConfig.
     * @param {string} [helpUrl] - URL to the help HTML file (defaults to HELP_HTML_URL constant)
     * @returns {Promise<Object>} Parsed help configuration object with topics and content
     */
    static async loadConfigFromHtml(helpUrl) {
        // console.log(`Fetching help url from "${helpUrl}"`)
        // no-cache: unlike the world data urls this fetch carries no cache-buster
        // version, and without it the browser's heuristic caching can serve a
        // stale help.html for days after an edit; this forces a conditional
        // revalidation (cheap 304 when unchanged) on every open
        // The help topics are rendered as markup, so the file must come from the app's own folders.
        assertTrustedAppDataUrl(helpUrl, 'the help file');
        const response = await fetch(helpUrl, { cache: 'no-cache' });
        // an error page (a 404, a 502 during a deploy) is not the help file: it used to be parsed as
        // one, each of its <h1>s a topic, and kept as the viewer until the page was reloaded; thrown,
        // the caller shows "Failed to load help content" and the next open tries again
        if (!response.ok) {
            throw new Error(`the help file answered ${response.status} ${response.statusText}`.trim());
        }
        const htmlContent = await response.text();
        return HelpViewer.parseHtmlToConfig(htmlContent);
    }




    // ==========================================
    // SHOW THE HELP DIALOG
    // ==========================================


    /**
     * Shows the help modal with optional topic navigation.
     * Uses the existing HelpViewer instance owned by this object.
     * @param {string} topicId - Optional topic id to navigate to
     * @param {string} anchor - Optional anchor within the topic
     */
    showModal(topicId = null, anchor = null) {
        // Navigate to specific topic if provided
        if (topicId) {
            // Wait a bit for modal to be shown before navigating
            setTimeout(() => {
                this.navigateToTopicById(topicId, anchor);
            }, 150);
        }

        this.showModalById(HelpViewer.MODAL_ID);
    }



    // ==========================================
    // RENDERING
    // ==========================================


    /**
     * Renders the help viewer HTML structure with sidebar and content area.
     * @private
     */
    renderHelp() {
        this.container.innerHTML = `
            <div class="help-viewer">
                <div class="help-search-bar">
                    <div class="help-search-input-wrapper">
                        <input
                            type="text"
                            class="form-control"
                            id="${ID_HELP_SEARCH}"
                            placeholder="Search help topics..."
                        >
                        <button class="help-search-clear" id="${ID_HELP_SEARCH_CLEAR}" title="Clear search">
                            <i class="bi bi-x"></i>
                        </button>
                    </div>
                </div>
                <div class="help-content-wrapper">
                    <div class="help-sidebar">
                        <div class="help-topics-list" id="${ID_HELP_TOPICS_LIST}">
                            ${this.renderTopicsList()}
                        </div>
                    </div>
                    <div class="help-main-content">
                        <div class="help-topic-content" id="${ID_HELP_TOPIC_CONTENT}">
                            <!-- Topic content will be inserted here -->
                        </div>
                    </div>
                </div>
            </div>
        `;
    }



    /**
     * Renders the content for a specific topic with search highlighting.
     * @param {Object} topic - The topic object to render
     * @param {string} topic.title - Topic title
     * @param {string} topic.content - Topic HTML content
     * @returns {string} HTML string for the topic content
     * @private
     */
    renderTopicContent(topic) {
        let content = `
            <div class="help-card">
                <div class="help-card-header">
                    <h4>${escapeHtml(topic.title)}</h4>
                </div>
                <div class="help-card-body">
                    ${topic.content}
                </div>
            </div>
        `;

        // Highlight search terms if there's an active search (performHelpSearch keeps the query in
        // helpSearchQuery; the highlighter touches only text, so the topic's links and markup survive)
        if (this.helpSearchQuery) {
            content = this.highlightSearchTermsInHtml(content, this.helpSearchQuery);
        }

        return content;
    }




    // ==========================================
    // WORK FUNCTIONS
    // ==========================================


    /**
     * Sets the search input value and performs the search.
     * Used for initialization or programmatic search.
     * @param {string} query - The search query to set and execute
     */
    setAndPerformHelpSearch(helpQuery) {
        const searchInput = this.container.querySelector('#' + ID_HELP_SEARCH);
        if (!searchInput) {
            return;
        }
        searchInput.value = helpQuery;
        this.performHelpSearch(helpQuery);
    }





    /**
     * Renders the list of topics in the sidebar.
     * @returns {string} HTML string for the topics list
     * @private
     */
    renderTopicsList() {
        return this.topics.map(topic => `
            <div class="${CLASS_HELP_TOPIC_ITEM} ${topic.id === this.currentTopic ? 'active' : ''}"
                 data-topic-id="${topic.id}">
                <i class="bi ${topic.icon || 'bi-question-circle'}"></i>
                <span class="help-topic-title">${escapeHtml(topic.title)}</span>
            </div>
        `).join('');
    }

    /**
     * Show a specific topic's content
     * @param {string} topicId - The topic ID to show
     * @param {string} anchor - Optional anchor to scroll to within the topic
     */
    showTopic(topicId, anchor = null) {
        const topic = this.topics.find(t => t.id === topicId);
        if (!topic) return;

        this.currentTopic = topicId;

        // Save current topic to state
        const stateManager = this.getStateManager();
        if (stateManager) {
            stateManager.setHelpLastTopic(topicId);
        }

        // Update active state in sidebar
        const topicItems = this.container.querySelectorAll('.' + CLASS_HELP_TOPIC_ITEM);
        topicItems.forEach(item => {
            if (item.dataset.topicId === topicId) {
                item.classList.add('active');
            } else {
                item.classList.remove('active');
            }
        });

        // Render topic content
        const contentContainer = this.container.querySelector('#' + ID_HELP_TOPIC_CONTENT);
        contentContainer.innerHTML = this.renderTopicContent(topic);

        // Scroll to anchor if specified
        if (anchor) {
            setTimeout(() => {
                const anchorElement = contentContainer.querySelector(`#${anchor}, [name="${anchor}"]`);
                if (anchorElement) {
                    anchorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        }
    }

    /**
     * Navigate to a topic by title and optional anchor
     * @param {string} topicTitle - The title of the topic to navigate to
     * @param {string} anchor - Optional anchor to scroll to within the topic
     */
    navigateToTopicByTitle(topicTitle, anchor = null) {
        const topic = this.topics.find(t =>
            t.title.toLowerCase() === topicTitle.toLowerCase()
        );

        if (topic) {
            this.showTopic(topic.id, anchor);
        } else {
            console.error(`ERROR: Topic title "${topicTitle}" not found`);
        }
    }


    /**
     * Navigate to a topic by title and optional anchor
     * @param {string} topicId - The id of the topic to navigate to
     * @param {string} anchor - Optional anchor to scroll to within the topic
     */
    navigateToTopicById(topicId, anchor = null) {
        const topic = this.topics.find(t =>
            t.id === topicId
        );

        if (topic) {
            this.showTopic(topic.id, anchor);
        } else {
            console.error(`ERROR: Topic id "${topicId}" not found`);
        }
    }





    /**
     * A topic's plain text, lowercased, as the help search matches it: the searchText that
     * parseHtmlToConfig works out once per topic (a topic built some other way gets it worked out on
     * first use, and kept).
     * @param {Object} topic - A help topic ({content, searchText?})
     * @returns {string}
     */
    static getTopicSearchText(topic) {
        if (typeof topic.searchText !== 'string') {
            topic.searchText = stripHtmlTags(topic.content || '').toLowerCase();
        }
        return topic.searchText;
    }


    /**
     * Performs search across all topics and updates the display.
     * Filters sidebar to show matching topics and displays first match.
     * @param {string} query - The search query
     */
    performHelpSearch(helpQuery) {
        this.helpSearchQuery = helpQuery.toLowerCase().trim();

        // update clear button
        this.updateHelpSearchClearButtonVisibility(this.helpSearchQuery);

        if (!this.helpSearchQuery || (this.helpSearchQuery=='')) {
            // Reset to show all topics
            this.showAllTopics();
            if (this.currentTopic) {
                this.showTopic(this.currentTopic);
            } else {
                // clear
                const contentContainer = this.container.querySelector('#' + ID_HELP_TOPIC_CONTENT);
                contentContainer.innerHTML = 'Select a topic on the left for help on a specific topic.'
            }
            return;
        }

        // Find matching topics
        const matchingTopics = this.topics.filter(topic => {
            const titleMatch = topic.title.toLowerCase().includes(this.helpSearchQuery);
            const contentMatch = HelpViewer.getTopicSearchText(topic).includes(this.helpSearchQuery);
            return titleMatch || contentMatch;
        });

        // Update sidebar to show only matching topics
        const topicsList = this.container.querySelector('#' + ID_HELP_TOPICS_LIST);
        if (matchingTopics.length > 0) {
            topicsList.innerHTML = matchingTopics.map(topic => {
                const titleHighlighted = this.highlightSearchTerms(topic.title, this.helpSearchQuery);
                return `
                    <div class="${CLASS_HELP_TOPIC_ITEM} ${topic.id === this.currentTopic ? 'active' : ''} highlight"
                         data-topic-id="${topic.id}">
                        <i class="bi ${topic.icon || 'bi-question-circle'}"></i>
                        <span class="help-topic-title">${titleHighlighted}</span>
                    </div>
                `;
            }).join('');

            // Show first matching topic
            if (matchingTopics.length > 0) {
                this.showTopic(matchingTopics[0].id);
            }
        } else {
            topicsList.innerHTML = '<div class="help-no-topics">No matching topics</div>';
            const contentContainer = this.container.querySelector('#' + ID_HELP_TOPIC_CONTENT);
            contentContainer.innerHTML = '<div class="help-no-topics">No matching topic content found</div>';
        }

        // Re-attach event listeners to new topic items
        this.attachTopicClickListeners();
    }



    /**
     * Shows all topics in the sidebar (resets search filter).
     */
    showAllTopics() {
        const topicsList = this.container.querySelector('#' + ID_HELP_TOPICS_LIST);
        topicsList.innerHTML = this.renderTopicsList();
        this.attachTopicClickListeners();
    }

    /**
     * Highlights search terms in a PLAIN TEXT string (a topic title) using mark tags.
     * The text is escaped piece by piece, so the result is safe HTML whatever the text or query is
     * (the old version ran the regex over HTML, so a query could land inside a tag or an entity).
     * @param {string} text - The plain text to highlight
     * @param {string} helpQuery - The search query to highlight
     * @returns {string} HTML with highlighted search terms
     * @private
     */
    highlightSearchTerms(text, helpQuery) {
        if (!helpQuery) return escapeHtml(text);

        const regex = new RegExp(`(${escapeRegex(helpQuery)})`, 'gi');
        // split with a capturing group: odd entries are the matches
        return String(text).split(regex).map((piece, index) => {
            return (index % 2 === 1) ? `<mark class="help-highlight">${escapeHtml(piece)}</mark>` : escapeHtml(piece);
        }).join('');
    }

    /**
     * Highlights search terms in an HTML string (topic content) using mark tags, touching only its
     * text nodes, so tags, attributes (data-app-command="search", class names) and entities survive.
     * @param {string} html - The HTML to highlight
     * @param {string} helpQuery - The search query to highlight
     * @returns {string} HTML with highlighted search terms
     * @private
     */
    highlightSearchTermsInHtml(html, helpQuery) {
        if (!helpQuery) return html;

        const template = document.createElement('template');
        template.innerHTML = html;
        const regex = new RegExp(`(${escapeRegex(helpQuery)})`, 'gi');
        const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
        const textNodes = [];
        while (walker.nextNode()) {
            textNodes.push(walker.currentNode);
        }
        for (const textNode of textNodes) {
            const pieces = textNode.nodeValue.split(regex);
            if (pieces.length < 2) continue;
            const fragment = document.createDocumentFragment();
            pieces.forEach((piece, index) => {
                if (index % 2 === 1) {
                    const mark = document.createElement('mark');
                    mark.className = 'help-highlight';
                    mark.textContent = piece;
                    fragment.appendChild(mark);
                } else if (piece) {
                    fragment.appendChild(document.createTextNode(piece));
                }
            });
            textNode.replaceWith(fragment);
        }
        return template.innerHTML;
    }






    // ==========================================
    // UI STUFF
    // ==========================================


    /**
     * Closes the help modal window programmatically.
     * @returns {boolean} True if modal was closed, false otherwise
     * @private
     */
    closeThisModalWindow() {
        return this.hideModalById(HelpViewer.MODAL_ID);
    }

    /**
     * Updates visibility of the search clear button based on query value.
     * @param {string} query - Current search query
     * @private
     */
    updateHelpSearchClearButtonVisibility(helpQuery) {
        const clearButton = this.container.querySelector('#' + ID_HELP_SEARCH_CLEAR);
        updateElementVisibilityBasedOnValue(clearButton, helpQuery);
    }



    /**
     * Updates the help viewer with a new configuration.
     * Resets current topic and search, then re-renders.
     * @param {Object} helpConfig - New help configuration with topics array
     */
    updateConfig(helpConfig) {
        this.helpConfig = helpConfig;
        this.topics = helpConfig.topics || [];
        this.currentTopic = null;
        this.helpSearchQuery = '';

        this.renderHelp();
        this.setupEventListeners();

        if (this.topics.length > 0) {
            this.showTopic(this.topics[0].id);
        }
    }



    // ==========================================
    // ACTION FUNCTIONS
    // ==========================================

    /**
     * Handle commands from help content links.
     * @param {string} command - The command to execute
     * @param {string} param - Optional parameter for the command
     */
    handleHelpCommand(command, param) {
        switch (command) {
            case 'gotoTopic':
                // Navigate to a topic by ID
                if (param) {
                    this.showTopic(param);
                }
                break;
            case 'helpSearch':
                // Run a search
                if (param) {
                    this.setAndPerformHelpSearch(param);
                }
                break;
            default:
                console.error(`ERROR: Unknown help command triggered in help: ${command}`);
        }
    }


    /**
     * Handle app commands from help content links.
     * @param {string} command - The command to execute
     * @param {string} param - Optional parameter for the command
     */
    handleAppCommand(command, param) {
        // Call back to the main application
        if (this.mapApp && typeof this.mapApp.handleAppCommand === 'function') {
            const retv = this.mapApp.handleAppCommand(command, param);
            if (retv) {
                this.closeThisModalWindow()
            }
        } else {
            console.error(`ERROR: handleAppCommand not implemented in mapApp.`);
        }
    }




}
